import React, { useEffect, useState } from "react";
import { X, Download, Upload, ChevronDown, Calendar } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../firebase/AuthProvider";
import NewParticipant from "../../../../interfaces/participant/NewParticipant";
import {
  getWaivers,
  postParticipant,
  GetParticipantByWaiverFamily,
} from "../../../../services/participantServices/ParticipantService";
import { getAllRegions } from "../../../../services/userServices/UserInfoService";
import Waiver from "../../../../interfaces/participant/Waiver";
import { DefaultUSCountryCodeId } from "../../../../Data/SD";
import { UserWaiverDTO } from "../../../../interfaces/Participant";
import "./AddParticipantModal.css";

interface OptionType {
  value: string;
  label: string;
}

interface AddParticipantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddParticipantModal: React.FC<AddParticipantModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<"single" | "bulk">("single");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [newParticipant, setNewParticipant] = useState<NewParticipant>({
    firstName: "",
    lastName: "",
    participantEmail: "",
    caseworker: "",
    waiverId: "",
    region: 0,
    mobileNumber: "",
    countryCodeId: DefaultUSCountryCodeId,
    linkageDate: null,
    cpocDate: null,
    cpocEndDate: null,
    certificationDate: null,
    initial: true,
    participantSsnLast4: "",
  });

  const [employees, setEmployees] = useState<UserWaiverDTO[] | null>([]);
  const [waivers, setWaivers] = useState<Waiver[] | null>([]);
  const [employeeOptions, setEmployeeOptions] = useState<OptionType[]>([]);
  const [waiverOptions, setWaiverOptions] = useState<OptionType[]>([]);
  const [regionOptions, setRegionOptions] = useState<OptionType[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});

  // Set default linkage date to today
  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      setNewParticipant((prev) => ({
        ...prev,
        linkageDate: today,
      }));
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchInfo = async () => {
      if (currentUser && isOpen) {
        const authToken: string = await currentUser.getIdToken();
        const waiversFetched = await getWaivers(authToken);
        if (waiversFetched.success) {
          setWaivers(waiversFetched.data);
          const formattedWaivers = waiversFetched.data.map((w: Waiver) => ({
            value: w.id.toString(),
            label: w.waiverName,
          }));
          setWaiverOptions(formattedWaivers);
        }
        GetAllRegions();
      }
    };
    fetchInfo();
  }, [currentUser, isOpen]);

  useEffect(() => {
    if (isOpen && newParticipant.waiverId) {
      GetEmployees();
    }
  }, [newParticipant.waiverId, isOpen]);

  const GetAllRegions = async () => {
    const token = await currentUser?.getIdToken();
    if (!token) return;
    getAllRegions(token).then((res) => {
      if (res.success) {
        const formattedRegions = res.data.map((region: any) => ({
          value: region.regionId.toString(),
          label: region.regionName,
        }));
        setRegionOptions(formattedRegions);
      }
    });
  };

  const GetEmployees = async () => {
    const token = await currentUser?.getIdToken();
    if (!token || !newParticipant.waiverId) return;

    const waiverCode = waivers?.find(
      (w) => w.id.toString() === newParticipant.waiverId
    )?.waiverName;

    if (waiverCode) {
      const employeesFetched = await GetParticipantByWaiverFamily(
        token,
        waiverCode
      );
      if (employeesFetched.success) {
        setEmployees(employeesFetched.data);
        const formattedEmployees = employeesFetched.data.map(
          (emp: UserWaiverDTO) => ({
            value: emp.id.toString(),
            label: emp.displayName,
          })
        );
        setEmployeeOptions(formattedEmployees);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let newValue = value;

    // Strip all whitespace for names & email
    if (name === "firstName" || name === "lastName" || name === "participantEmail") {
      newValue = value.replace(/\s/g, "");
    } else if (name === "mobileNumber") {
      // Digits only, max 10
      newValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "participantSsnLast4") {
      // Digits only, max 4 (leading zeros allowed)
      newValue = value.replace(/\D/g, "").slice(0, 4);
    }

    setNewParticipant((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const dateValue = value ? new Date(value) : null;
    setNewParticipant((prev) => ({
      ...prev,
      [name]: dateValue,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleWaiverChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setNewParticipant((prev) => ({
      ...prev,
      waiverId: selectedId || "",
      caseworker: "", // Reset caseworker when waiver changes
    }));
    setEmployeeOptions([]);
    setErrors((prev) => ({ ...prev, waiverId: undefined, caseworker: undefined }));
  };

  const handleAssigneeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setNewParticipant((prev) => ({
      ...prev,
      caseworker: e.target.value || "",
    }));
    if (errors.caseworker) {
      setErrors((prev) => ({ ...prev, caseworker: undefined }));
    }
  };

  const handleRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setNewParticipant((prev) => ({
      ...prev,
      region: e.target.value ? parseInt(e.target.value) : 0,
    }));
    if (errors.region) {
      setErrors((prev) => ({ ...prev, region: undefined }));
    }
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidUSPhone = (phone: string): boolean => {
    return /^\d{10}$/.test(phone);
  };

  const validateFields = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!newParticipant.firstName || newParticipant.firstName === "") {
      newErrors.firstName = "First Name is required.";
    }
    if (!newParticipant.lastName || newParticipant.lastName === "") {
      newErrors.lastName = "Last Name is required.";
    }

    const emailSanitized = (newParticipant.participantEmail || "").replace(/\s/g, "");
    if (emailSanitized) {
      if (!isValidEmail(emailSanitized)) newErrors.participantEmail = "Invalid email address.";
    }

    const phoneSanitized = (newParticipant.mobileNumber || "").replace(/\D/g, "");
    if (phoneSanitized) {
      if (!isValidUSPhone(phoneSanitized)) newErrors.mobileNumber = "Phone number must be 10 digits (US).";
    }

    if (newParticipant.participantSsnLast4 && newParticipant.participantSsnLast4 !== "") {
      if (!/^\d{4}$/.test(newParticipant.participantSsnLast4)) {
        newErrors.participantSsnLast4 = "SSN must be exactly 4 digits.";
      }
    }

    if (newParticipant.waiverId === "") {
      newErrors.waiverId = "Participant Waiver is required.";
    }
    if (newParticipant.caseworker === "") {
      newErrors.caseworker = "Support Coordinator is required.";
    }
    if (newParticipant.region === 0) {
      newErrors.region = "Region is required.";
    }
    if (!newParticipant.linkageDate) {
      newErrors.linkageDate = "Linkage Date is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDateForInput = (date: Date | null): string => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateFields()) {
      return;
    }

    setIsLoading(true);
    try {
      const authToken = await currentUser?.getIdToken();
      if (!authToken) {
        toast.error("Authentication error");
        return;
      }

      const response = await postParticipant(newParticipant, authToken);
      if (response.success) {
        toast.success("Participant created successfully!");
        handleCancel();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error(response.message || "Failed to create participant");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to create participant");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setNewParticipant({
      firstName: "",
      lastName: "",
      participantEmail: "",
      caseworker: "",
      waiverId: "",
      region: 0,
      mobileNumber: "",
      countryCodeId: DefaultUSCountryCodeId,
      linkageDate: new Date(),
      cpocDate: null,
      cpocEndDate: null,
      certificationDate: null,
      initial: true,
      participantSsnLast4: "",
    });
    setErrors({});
    setActiveTab("single");
    onClose();
  };

  const downloadCsvTemplate = () => {
    const headers = [
      "First Name",
      "Last Name", 
      "Participant Email",
      "Mobile Telephone Number",
      "Participant Waiver",
      "Support Coordinator",
      "Region",
      "Linkage Date",
      "Certification Date",
      "CPOC Start Date",
      "CPOC End Date",
      "Participant SSN Last 4 Digit"
    ];
    
    const csvContent = [
      headers.join(","),
      `John,Doe,john.doe@example.com,5551234567,SCA,SUPPORT_COORDINATOR_NAME,REGION_NAME,${new Date().toISOString().split('T')[0]},,,,,1234`
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "participant_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV template downloaded successfully!");
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto no-scrollbar p-4"
        onClick={handleCancel}
      >
        <div
          className="relative w-full max-w-[55rem] my-auto rounded-2xl bg-white shadow-xl overflow-y-auto no-scrollbar"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white rounded-t-2xl z-10">
            <div className="flex items-center justify-between border-b border-[#E5E7EB] py-4 px-6">
              <div>
                <h2 className="" style={{
                  color: "#0A0A0A",
                  fontFamily: "Poppins",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  lineHeight: "1.75rem" /* 140% */
                }}>
                  New Participant
                </h2>
                <p className="m-0 mt-1 text-[#4A5565] font-poppins" style={{ fontSize: "0.875rem", fontWeight: 400, lineHeight: "1.25rem" }}>
                  Every new participant is a new person we can help together
                </p>
              </div>
              <button
                onClick={handleCancel}
                className="flex items-center text-[#0A0A0A] hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex px-6 pt-3 gap-2">
              <button
                onClick={() => setActiveTab("single")}
                className={`px-4 py-2 font-poppins rounded-[0.62rem] transition-colors ${activeTab === "single"
                  ? "bg-[#7E57C2] text-white"
                  : "bg-[#F3F4F6] text-[#747474] hover:bg-gray-200"
                  }`}
              >
                Add Participant
              </button>
              <button
                onClick={() => setActiveTab("bulk")}
                className={`px-4 py-2 text-base font-poppins rounded-[0.62rem] transition-colors ${activeTab === "bulk"
                  ? "bg-[#7E57C2] text-white"
                  : "bg-[#F3F4F6] text-[#747474] hover:bg-gray-200"
                  }`}
              >
                Upload Multiple (CSV)
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {activeTab === "single" ? (
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Participant First Name */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      Participant First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={newParticipant.firstName}
                      onChange={handleChange}
                      placeholder="First Name"
                      className={`w-full px-3 py-2 border ${errors.firstName
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                        } rounded-[0.625rem] text-base placeholder:text-gray-400 focus:outline-none focus:ring-1`}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                    )}
                  </div>

                  {/* Participant Last Name */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      Participant Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={newParticipant.lastName}
                      onChange={handleChange}
                      placeholder="Last Name"
                      className={`w-full px-3 py-2 border ${errors.lastName
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                        } rounded-[0.625rem] text-base placeholder:text-gray-400 focus:outline-none focus:ring-1`}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                    )}
                  </div>

                  {/* Participant Email */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      Participant Email <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <input
                      type="email"
                      name="participantEmail"
                      value={newParticipant.participantEmail || ""}
                      onChange={handleChange}
                      placeholder="Email"
                      className={`w-full px-3 py-2 border ${errors.participantEmail
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                        } rounded-[0.625rem] text-base placeholder:text-gray-400 focus:outline-none focus:ring-1`}
                    />
                    {errors.participantEmail && (
                      <p className="text-red-500 text-xs mt-1">{errors.participantEmail}</p>
                    )}
                  </div>

                  {/* Mobile Telephone Number */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      Mobile Telephone Number <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <input
                      type="text"
                      name="mobileNumber"
                      value={newParticipant.mobileNumber || ""}
                      onChange={handleChange}
                      placeholder="Mobile Telephone Number"
                      inputMode="numeric"
                      maxLength={10}
                      className={`w-full px-3 py-2 border ${errors.mobileNumber
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                        } rounded-[0.625rem] text-base placeholder:text-gray-400 focus:outline-none focus:ring-1`}
                    />
                    {errors.mobileNumber && (
                      <p className="text-red-500 text-xs mt-1">{errors.mobileNumber}</p>
                    )}
                  </div>

                  {/* Participant Waiver */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      Participant Waiver <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={newParticipant.waiverId || ""}
                        onChange={handleWaiverChange}
                        className={`w-full px-3 py-2 border ${errors.waiverId
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                          } rounded-[0.625rem] text-base focus:outline-none focus:ring-1 appearance-none bg-white`}
                      >
                        <option value="">Select waiver</option>
                        {waivers?.map((w) => (
                          <option key={w.id} value={w.id.toString()}>
                            {w.waiverName}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                    {errors.waiverId && (
                      <p className="text-red-500 text-xs mt-1">{errors.waiverId}</p>
                    )}
                  </div>

                  {/* Support Coordinator */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      Support Coordinator<span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={newParticipant.caseworker || ""}
                        onChange={handleAssigneeChange}
                        disabled={!newParticipant.waiverId}
                        className={`w-full px-3 py-2 border ${errors.caseworker
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                          } rounded-[0.625rem] text-base focus:outline-none focus:ring-1 appearance-none bg-white ${!newParticipant.waiverId ? "bg-gray-100 cursor-not-allowed" : ""
                          }`}
                      >
                        <option value="">Select support coordinator</option>
                        {employeeOptions.map((emp) => (
                          <option key={emp.value} value={emp.value}>
                            {emp.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                    {errors.caseworker && (
                      <p className="text-red-500 text-xs mt-1">{errors.caseworker}</p>
                    )}
                  </div>

                  {/* Region */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      Region <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={newParticipant.region || ""}
                        onChange={handleRegionChange}
                        className={`w-full px-3 py-2 border ${errors.region
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                          } rounded-[0.625rem] text-base focus:outline-none focus:ring-1 appearance-none bg-white`}
                      >
                        <option value="">Select region</option>
                        {regionOptions.map((region) => (
                          <option key={region.value} value={region.value}>
                            {region.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                    {errors.region && (
                      <p className="text-red-500 text-xs mt-1">{errors.region}</p>
                    )}
                  </div>

                  {/* Linkage Date */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      Linkage Date <span className="text-red-500">*</span>
                    </label>

                    <div className="relative">
                      <input
                        type="date"
                        name="linkageDate"
                        value={
                          newParticipant.linkageDate
                            ? new Date(newParticipant.linkageDate)
                              .toISOString()
                              .split("T")[0]
                            : ""
                        }
                        onChange={handleDateChange}
                        placeholder="12/25/2025"
                        className={`w-full px-3 py-2 pr-10 border ${errors.linkageDate
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                          } rounded-[0.625rem] text-base focus:outline-none focus:ring-1`}
                      />

                      <Calendar
                        size={18}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>

                    {errors.linkageDate && (
                      <p className="text-red-500 text-xs mt-1">{errors.linkageDate}</p>
                    )}
                  </div>


                  {/* Certification Date */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      Certification Date <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="certificationDate"
                        value={
                          newParticipant.certificationDate
                            ? new Date(newParticipant.certificationDate).toISOString().split("T")[0]
                            : ""
                        }
                        onChange={handleDateChange}
                        placeholder="12/25/2025"
                        className="w-full px-3 py-2 pr-10 border border-[#D1D5DC] rounded-[0.625rem] text-base focus:outline-none focus:ring-1 focus:border-purple-500 focus:ring-purple-500"
                      />
                      <Calendar
                        size={18}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  {/* CPOC Start Date */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      CPOC Start Date <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="cpocDate"
                        value={
                          newParticipant.cpocDate
                            ? new Date(newParticipant.cpocDate).toISOString().split("T")[0]
                            : ""
                        }
                        onChange={handleDateChange}
                        placeholder="12/25/2025"
                        className="w-full px-3 py-2 pr-10 border border-[#D1D5DC] rounded-[0.625rem] text-base focus:outline-none focus:ring-1 focus:border-purple-500 focus:ring-purple-500"
                      />
                      <Calendar
                        size={18}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  {/* CPOC End Date */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      CPOC End Date <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="cpocEndDate"
                        value={
                          newParticipant.cpocEndDate
                            ? new Date(newParticipant.cpocEndDate).toISOString().split("T")[0]
                            : ""
                        }
                        onChange={handleDateChange}
                        placeholder="12/25/2025"
                        className="w-full px-3 py-2 pr-10 border border-[#D1D5DC] rounded-[0.625rem] text-base focus:outline-none focus:ring-1 focus:border-purple-500 focus:ring-purple-500"
                      />
                      <Calendar
                        size={18}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  {/* Participant SSN Last 4 Digit */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-1">
                      Participant SSN Last 4 Digit <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <input
                      type="text"
                      name="participantSsnLast4"
                      value={newParticipant.participantSsnLast4 || ""}
                      onChange={handleChange}
                      placeholder="Last 4 digits of SSN"
                      maxLength={4}
                      pattern="\d{4}"
                      inputMode="numeric"
                      className={`w-full px-3 py-2 border ${errors.participantSsnLast4
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                        } rounded-[0.625rem] text-base placeholder:text-gray-400 focus:outline-none focus:ring-1`}
                    />
                    {errors.participantSsnLast4 && (
                      <p className="text-red-500 text-xs mt-1">{errors.participantSsnLast4}</p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-4 border-[#E5E7EB]">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-2 bg-[#7E57C2] text-white font-poppins text-base font-medium rounded-[0.625rem] hover:bg-[#6D4CAB] transition-colors"
                    >
                    {isLoading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                {/* Instructions Section */}
                <div style={{border: "1px solid #C9C9C9;"}} className="bg-[#F4F4F4] border  rounded-[0.625rem] p-2">
                  <h3 className="text-[#0A0A0A] pl-1 font-poppins text-base font-semibold mb-1">
                    Instructions:
                  </h3>
                  <ol className="space-y-0.5 text-[#747474] font-poppins text-base font-normal pl-1 leading-tight">
                    <li>Step 1 - Download the CSV template</li>
                    <li>Step 2 - Fill in participant information</li>
                    <li>Step 3 - Upload the completed CSV file</li>
                  </ol>
                </div>

                {/* Download CSV Template Button */}
                <button
                  type="button"
                  onClick={downloadCsvTemplate}
                  className="w-full flex items-center justify-center gap-2"
                  style={{
                    borderRadius: "0.625rem",
                    border: "2px solid var(--primary-button, #7E57C2)",
                    background: "#FFF",
                    color: "#7E57C2",
                    textAlign: "center",
                    fontFamily: "Poppins",
                    fontSize: "1rem",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "1.25rem",
                    padding: "0.75rem 1rem"
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="#7E57C2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M4.66699 6.66699L8.00033 10.0003L11.3337 6.66699" stroke="#7E57C2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M8 10V2" stroke="#7E57C2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>

                  <span style={{ fontSize: "1rem", fontWeight: 400 }}>Download CSV Template</span>
                </button>

                {/* Upload Completed CSV Section */}
                <div>
                  <h3 className="text-[#364153] mb-3 font-poppins" style={{ fontSize: "1.125rem", fontWeight: 400, }}>
                    Upload Completed CSV
                  </h3>
                  <div
                    className={`border-2 rounded-[0.625rem] p-8 text-center transition-colors ${isDragging
                      ? "border-[#7E57C2] bg-[#F3E8FF]"
                      : "border-[#C9C9C9] bg-[#F9FAFB]"
                      }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      const file = e.dataTransfer.files[0];
                      if (file && file.type === "text/csv") {
                        setCsvFile(file);
                      } else {
                        toast.error("Please upload a CSV file only");
                      }
                    }}
                  >
                    <input
                      type="file"
                      id="csv-upload"
                      accept=".csv"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setCsvFile(file);
                        }
                      }}
                    />
                    {csvFile ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center">
                          <Upload size={32} className="text-[#7E57C2]" />
                        </div>
                        <p className="text-[#0A0A0A] font-poppins text-base font-medium">
                          {csvFile.name}
                        </p>
                        <button
                          type="button"
                          onClick={() => setCsvFile(null)}
                          className="text-[#7E57C2] font-poppins text-base hover:underline"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <label htmlFor="csv-upload" className="cursor-pointer">
                        <div className="flex flex-col items-center gap-0.5">
                          {/* <Upload size={32} className="text-[#747474]" /> */}
                          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                            <path d="M42 30V38C42 39.0609 41.5786 40.0783 40.8284 40.8284C40.0783 41.5786 39.0609 42 38 42H10C8.93913 42 7.92172 41.5786 7.17157 40.8284C6.42143 40.0783 6 39.0609 6 38V30" stroke="#99A1AF" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M34 16L24 6L14 16" stroke="#99A1AF" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M24 6V30" stroke="#99A1AF" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          <p className="text-[#4A5565] font-poppins mb-0" style={{ fontSize: "1.125rem", fontWeight: 400, }}>
                            Click to upload or drag and drop
                          </p>
                          <p className="text-[#6A7282] font-poppins text-xs" style={{ fontSize: "1rem", fontWeight: 400, }}>
                            CSV files only
                          </p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* Bottom Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="font-poppins text-base hover:bg-gray-200 transition-colors"
                    style={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      borderRadius: "0.625rem",
                      border: "1px solid #D1D5DC",
                      padding: "0.625rem 1.5rem",
                      color: "#747474"
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!csvFile) {
                        toast.error("Please upload a CSV file");
                        return;
                      }
                      toast.info("CSV upload functionality will be implemented");
                    }}
                    className="font-poppins text-[#747474] text-base font-medium transition-colors"
                    style={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      display: "flex",
                      padding: "0.6875rem 2.92481rem 0.6875rem 3.01269rem",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "0.625rem",
                      background: "#F4F4F4"
                    }}
                  >
                    Import
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div >
    </>
  );
};

export default AddParticipantModal;

